import { useState, useEffect, useRef } from "react";
import {
  FiPlus, FiEdit2, FiTrash2, FiEye, FiDownload,
  FiFileText, FiBookOpen, FiLayers, FiSearch,
  FiFilter, FiX, FiUpload, FiCheckCircle,
  FiAlertTriangle, FiStar, FiGlobe, FiClock,
  FiRefreshCw,
} from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi2";
import "./AdminInsights.css";

/* ── Constants ─────────────────────────────────────────────── */
const TYPE_META = {
  "case-study": { label: "Case Study",  icon: <FiFileText />,  color: "#0F4C5C" },
  "blog":        { label: "Blog",        icon: <FiBookOpen />,  color: "#f5a623" },
  "whitepaper":  { label: "Whitepaper",  icon: <FiLayers />,    color: "#4FC3F7" },
};

const EMPTY_FORM = {
  title: "", type: "case-study", excerpt: "",
  tags: "", featured: false, order: 0,
  isPublished: true, file: null,
};

/* ── Toast notification ────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`ai-toast ai-toast--${type}`}>
      {type === "success" ? <FiCheckCircle /> : <FiAlertTriangle />}
      <span>{message}</span>
      <button className="ai-toast__close" onClick={onClose}><FiX /></button>
    </div>
  );
}

/* ── Confirm dialog ────────────────────────────────────────── */
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="ai-overlay" onClick={onCancel}>
      <div className="ai-confirm" onClick={e => e.stopPropagation()}>
        <div className="ai-confirm__icon"><FiAlertTriangle /></div>
        <h3>Confirm Delete</h3>
        <p>{message}</p>
        <div className="ai-confirm__actions">
          <button className="ai-btn ai-btn--danger" onClick={onConfirm}>Delete</button>
          <button className="ai-btn ai-btn--ghost" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function AdminInsights() {
  const [insights,    setInsights]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [showForm,    setShowForm]    = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [saving,      setSaving]      = useState(false);
  const [search,      setSearch]      = useState("");
  const [filterType,  setFilterType]  = useState("all");
  const [filterPub,   setFilterPub]   = useState("all");
  const [toast,       setToast]       = useState(null);
  const [confirmDel,  setConfirmDel]  = useState(null);
  const [dragOver,    setDragOver]    = useState(false);
  const fileRef = useRef();

  useEffect(() => { fetchInsights(); }, []);

  /* ── Data fetching ────────────────────────────────────────── */
  const fetchInsights = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    const token = localStorage.getItem("adminToken");
    try {
      const res  = await fetch(`${import.meta.env.VITE_API_URL}/api/insights/admin/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setInsights(data.data);
      else showToast(data.message || "Failed to load insights", "error");
    } catch {
      showToast("Network error. Please retry.", "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /* ── Toast helper ─────────────────────────────────────────── */
  const showToast = (message, type = "success") =>
    setToast({ message, type, id: Date.now() });

  /* ── Form helpers ─────────────────────────────────────────── */
  const set = (k, v) => setFormData(f => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (insight) => {
    setEditing(insight);
    setFormData({
      title:       insight.title,
      type:        insight.type,
      excerpt:     insight.excerpt,
      tags:        insight.tags?.join(", ") || "",
      featured:    insight.featured,
      order:       insight.order,
      isPublished: insight.isPublished,
      file:        null,
    });
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); setFormData(EMPTY_FORM); };

  /* ── Submit ───────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file && !editing) { showToast("Please upload a file.", "error"); return; }
    setSaving(true);
    const token = localStorage.getItem("adminToken");
    const form  = new FormData();
    form.append("title",       formData.title);
    form.append("type",        formData.type);
    form.append("excerpt",     formData.excerpt);
    form.append("tags",        JSON.stringify(formData.tags.split(",").map(t => t.trim()).filter(Boolean)));
    form.append("featured",    formData.featured);
    form.append("order",       formData.order);
    form.append("isPublished", formData.isPublished);
    if (formData.file) form.append("file", formData.file);

    const url    = editing
      ? `${import.meta.env.VITE_API_URL}/api/insights/admin/${editing._id}`
      : `${import.meta.env.VITE_API_URL}/api/insights/admin`;
    const method = editing ? "PUT" : "POST";

    try {
      const res  = await fetch(url, { method, headers: { Authorization: `Bearer ${token}` }, body: form });
      const data = await res.json();
      if (data.success) {
        showToast(editing ? "Insight updated successfully." : "Insight created successfully.");
        closeForm();
        fetchInsights();
      } else {
        showToast(data.message || "Error saving insight.", "error");
      }
    } catch {
      showToast("Network error. Please retry.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ───────────────────────────────────────────────── */
  const handleDelete = (id) => setConfirmDel(id);

  const confirmDelete = async () => {
    const token = localStorage.getItem("adminToken");
    try {
      const res  = await fetch(`${import.meta.env.VITE_API_URL}/api/insights/admin/${confirmDel}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) { showToast("Insight deleted."); fetchInsights(); }
      else showToast(data.message || "Delete failed.", "error");
    } catch {
      showToast("Network error.", "error");
    } finally {
      setConfirmDel(null);
    }
  };

  /* ── File drag & drop ─────────────────────────────────────── */
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) set("file", file);
  };

  /* ── Filtered list ────────────────────────────────────────── */
  const filtered = insights.filter(i => {
    const matchSearch = i.title.toLowerCase().includes(search.toLowerCase()) ||
                        i.excerpt?.toLowerCase().includes(search.toLowerCase());
    const matchType   = filterType === "all" || i.type === filterType;
    const matchPub    = filterPub  === "all" ||
                        (filterPub === "published" && i.isPublished) ||
                        (filterPub === "draft"     && !i.isPublished);
    return matchSearch && matchType && matchPub;
  });

  const counts = {
    all:        insights.length,
    published:  insights.filter(i => i.isPublished).length,
    draft:      insights.filter(i => !i.isPublished).length,
    featured:   insights.filter(i => i.featured).length,
  };

  /* ── Loading ─────────────────────────────────────────────── */
  if (loading) return (
    <div className="ai-page ai-page--loading">
      <div className="ai-spinner" />
      <span>Loading insights…</span>
    </div>
  );

  /* ═══════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════ */
  return (
    <div className="ai-page">

      {/* ── TOAST ──────────────────────────────────────────── */}
      {toast && (
        <Toast key={toast.id} message={toast.message} type={toast.type}
          onClose={() => setToast(null)} />
      )}

      {/* ── CONFIRM ────────────────────────────────────────── */}
      {confirmDel && (
        <ConfirmDialog
          message="This action cannot be undone. The insight and its file will be permanently removed."
          onConfirm={confirmDelete}
          onCancel={() => setConfirmDel(null)}
        />
      )}

      {/* ── PAGE HEADER ────────────────────────────────────── */}
      <header className="ai-header">
        <div className="ai-header__left">
          <span className="ai-eyebrow">
            <HiOutlineSparkles className="ai-eyebrow__icon" />
            Content Management
          </span>
          <h1 className="ai-header__title">
            Insights <span>&amp; Publications</span>
          </h1>
        </div>

        <div className="ai-header__right">
          <button
            className={`ai-btn ai-btn--icon ${refreshing ? "ai-btn--spinning" : ""}`}
            onClick={() => fetchInsights(true)}
            disabled={refreshing}
            title="Refresh"
          >
            <FiRefreshCw />
          </button>
          <button className="ai-btn ai-btn--primary" onClick={openCreate}>
            <FiPlus /> Create Insight
          </button>
        </div>
      </header>

      {/* ── SUMMARY STRIPS ─────────────────────────────────── */}
      <div className="ai-summary">
        {[
          { label: "Total",     val: counts.all,       icon: <FiLayers />,     accent: "#0F4C5C" },
          { label: "Published", val: counts.published, icon: <FiGlobe />,      accent: "#27ae60" },
          { label: "Drafts",    val: counts.draft,     icon: <FiClock />,      accent: "#f5a623" },
          { label: "Featured",  val: counts.featured,  icon: <FiStar />,       accent: "#CE93D8" },
        ].map(({ label, val, icon, accent }) => (
          <div key={label} className="ai-summary__card" style={{ "--accent": accent }}>
            <div className="ai-summary__icon">{icon}</div>
            <div className="ai-summary__body">
              <span className="ai-summary__val">{val}</span>
              <span className="ai-summary__label">{label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── TOOLBAR ────────────────────────────────────────── */}
      <div className="ai-toolbar">
        <div className="ai-search">
          <FiSearch className="ai-search__icon" />
          <input
            className="ai-search__input"
            placeholder="Search by title or excerpt…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="ai-search__clear" onClick={() => setSearch("")}><FiX /></button>
          )}
        </div>

        <div className="ai-filters">
          <FiFilter className="ai-filters__icon" />
          <select className="ai-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            {Object.entries(TYPE_META).map(([v, m]) => (
              <option key={v} value={v}>{m.label}</option>
            ))}
          </select>
          <select className="ai-select" value={filterPub} onChange={e => setFilterPub(e.target.value)}>
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Drafts</option>
          </select>
        </div>

        <span className="ai-result-count">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {/* ── TABLE ──────────────────────────────────────────── */}
      <div className="ai-table-wrap">
        {filtered.length === 0 ? (
          <div className="ai-empty">
            <FiFileText className="ai-empty__icon" />
            <h3>No insights found</h3>
            <p>{search || filterType !== "all" || filterPub !== "all"
              ? "Try adjusting your filters."
              : "Click Create Insight to publish your first one."}
            </p>
            {!search && filterType === "all" && filterPub === "all" && (
              <button className="ai-btn ai-btn--primary" onClick={openCreate}>
                <FiPlus /> Create Insight
              </button>
            )}
          </div>
        ) : (
          <table className="ai-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th><FiEye className="ai-th-icon" /> Views</th>
                <th><FiDownload className="ai-th-icon" /> Downloads</th>
                <th>Status</th>
                <th>Published</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ins, i) => {
                const meta = TYPE_META[ins.type] || TYPE_META["blog"];
                return (
                  <tr key={ins._id} style={{ animationDelay: `${i * 0.03}s` }}>
                    <td className="ai-table__title-cell">
                      <div className="ai-table__title-wrap">
                        {ins.featured && <FiStar className="ai-table__star" />}
                        <div>
                          <div className="ai-table__title">{ins.title}</div>
                          {ins.tags?.length > 0 && (
                            <div className="ai-table__tags">
                              {ins.tags.slice(0, 3).map(t => (
                                <span key={t} className="ai-tag">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td>
                      <span className="ai-type-badge" style={{ "--t-color": meta.color }}>
                        {meta.icon} {meta.label}
                      </span>
                    </td>

                    <td className="ai-table__num">{ins.viewCount || 0}</td>
                    <td className="ai-table__num">{ins.downloadCount || 0}</td>

                    <td>
                      <span className={`ai-status ${ins.isPublished ? "ai-status--pub" : "ai-status--draft"}`}>
                        {ins.isPublished ? "Published" : "Draft"}
                      </span>
                    </td>

                    <td className="ai-table__date">
                      {new Date(ins.publishedAt).toLocaleDateString("en-IN", {
                        day: "numeric", month: "short", year: "numeric",
                      })}
                    </td>

                    <td>
                      <div className="ai-table__actions">
                        <button className="ai-action-btn ai-action-btn--edit" onClick={() => openEdit(ins)} title="Edit">
                          <FiEdit2 />
                        </button>
                        <button className="ai-action-btn ai-action-btn--del" onClick={() => handleDelete(ins._id)} title="Delete">
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════
          MODAL FORM
      ══════════════════════════════════════════════════════ */}
      {showForm && (
        <div className="ai-overlay" onClick={closeForm}>
          <div className="ai-modal" onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div className="ai-modal__header">
              <div>
                <span className="ai-eyebrow">{editing ? "Edit" : "Create"}</span>
                <h2 className="ai-modal__title">
                  {editing ? "Update Insight" : "New Insight"}
                </h2>
              </div>
              <button className="ai-modal__close" onClick={closeForm}><FiX /></button>
            </div>

            <form className="ai-form" onSubmit={handleSubmit} noValidate>

              {/* Title */}
              <div className="ai-field">
                <label className="ai-label">Title *</label>
                <input
                  className="ai-input"
                  value={formData.title}
                  onChange={e => set("title", e.target.value)}
                  placeholder="e.g. Reimagining Enterprise AI Adoption"
                  required
                />
              </div>

              {/* Type + Order */}
              <div className="ai-field-row">
                <div className="ai-field">
                  <label className="ai-label">Type *</label>
                  <select className="ai-input ai-input--select" value={formData.type} onChange={e => set("type", e.target.value)}>
                    {Object.entries(TYPE_META).map(([v, m]) => (
                      <option key={v} value={v}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="ai-field">
                  <label className="ai-label">Display Order</label>
                  <input
                    className="ai-input"
                    type="number"
                    value={formData.order}
                    onChange={e => set("order", parseInt(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div className="ai-field">
                <label className="ai-label">Excerpt *</label>
                <textarea
                  className="ai-input ai-input--textarea"
                  rows={3}
                  value={formData.excerpt}
                  onChange={e => set("excerpt", e.target.value)}
                  placeholder="Brief summary visible on the insights listing page…"
                  required
                  maxLength={300}
                />
                <span className="ai-char-count">{formData.excerpt.length} / 300</span>
              </div>

              {/* Tags */}
              <div className="ai-field">
                <label className="ai-label">Tags <span className="ai-label--hint">(comma-separated)</span></label>
                <input
                  className="ai-input"
                  value={formData.tags}
                  onChange={e => set("tags", e.target.value)}
                  placeholder="AI, Cloud, Digital Transformation"
                />
                {formData.tags && (
                  <div className="ai-tag-preview">
                    {formData.tags.split(",").map(t => t.trim()).filter(Boolean).map(t => (
                      <span key={t} className="ai-tag">{t}</span>
                    ))}
                  </div>
                )}
              </div>

              {/* File upload */}
              <div className="ai-field">
                <label className="ai-label">
                  File (PDF / DOC / DOCX){!editing && " *"}
                </label>
                <div
                  className={`ai-dropzone ${dragOver ? "ai-dropzone--active" : ""} ${formData.file ? "ai-dropzone--filled" : ""}`}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileRef.current.click()}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    hidden
                    onChange={e => set("file", e.target.files[0])}
                  />
                  {formData.file ? (
                    <>
                      <FiCheckCircle className="ai-dropzone__icon ai-dropzone__icon--ok" />
                      <span className="ai-dropzone__name">{formData.file.name}</span>
                      <button
                        type="button"
                        className="ai-dropzone__remove"
                        onClick={e => { e.stopPropagation(); set("file", null); }}
                      >
                        <FiX /> Remove
                      </button>
                    </>
                  ) : (
                    <>
                      <FiUpload className="ai-dropzone__icon" />
                      <span className="ai-dropzone__text">
                        <strong>Click to upload</strong> or drag & drop
                      </span>
                      <span className="ai-dropzone__hint">
                        {editing ? "Leave empty to keep existing file" : "PDF, DOC, DOCX — max 10 MB"}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Toggles */}
              <div className="ai-toggles">
                <label className={`ai-toggle ${formData.featured ? "ai-toggle--on" : ""}`}>
                  <div className="ai-toggle__track" onClick={() => set("featured", !formData.featured)}>
                    <div className="ai-toggle__thumb" />
                  </div>
                  <div className="ai-toggle__info">
                    <span className="ai-toggle__label"><FiStar /> Featured</span>
                    <span className="ai-toggle__desc">Appears at the top of the listing</span>
                  </div>
                </label>

                <label className={`ai-toggle ${formData.isPublished ? "ai-toggle--on" : ""}`}>
                  <div className="ai-toggle__track" onClick={() => set("isPublished", !formData.isPublished)}>
                    <div className="ai-toggle__thumb" />
                  </div>
                  <div className="ai-toggle__info">
                    <span className="ai-toggle__label"><FiGlobe /> Published</span>
                    <span className="ai-toggle__desc">Visible to the public</span>
                  </div>
                </label>
              </div>

              {/* Actions */}
              <div className="ai-form__actions">
                <button type="submit" className="ai-btn ai-btn--primary" disabled={saving}>
                  {saving
                    ? <><span className="ai-btn-spinner" /> Saving…</>
                    : <>{editing ? <FiEdit2 /> : <FiPlus />} {editing ? "Update Insight" : "Create Insight"}</>
                  }
                </button>
                <button type="button" className="ai-btn ai-btn--ghost" onClick={closeForm}>
                  Cancel
                </button>
              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
}